import { create } from 'zustand'
import arabic from './ar.json'

export type Language = 'en' | 'ar'

const STORAGE_KEY = 'octapos.language'

/**
 * English, and Arabic as spoken in Lebanon.
 *
 * The key of a translation **is the English string**, so `t('Receive stock')` looks
 * `"Receive stock"` up in `ar.json`. There is no English file and there never will be: English is
 * the fallback, which means a screen nobody has translated yet reads in English rather than showing
 * a bare key to a cashier with a queue waiting. It also means a string can be moved, reworded or
 * deleted without a second file drifting out of step behind it.
 */
const dictionaries: Record<Language, Record<string, string>> = {
  en: {},
  ar: arabic as Record<string, string>,
}

function initialLanguage(): Language {
  const stored = localStorage.getItem(STORAGE_KEY)
  if (stored === 'ar' || stored === 'en') return stored

  // English by default, including for a browser set to Arabic. The shop's own language is a
  // decision the owner makes once for the till, not something inferred from whichever phone
  // happens to be at the counter.
  return 'en'
}

/**
 * Sets the writing direction on the document.
 *
 * `dir` on the root rather than a class, because it is what the browser itself uses to lay out
 * text, mirror scrollbars and decide where a caret goes. Tailwind's logical properties (`ms-`,
 * `pe-`, `text-start`) all follow from it, so a screen written with those flips with no further
 * work — and one written with `ml-` and `text-left` does not, which is why the components the till
 * uses were converted.
 */
function apply(language: Language): void {
  document.documentElement.lang = language
  document.documentElement.dir = language === 'ar' ? 'rtl' : 'ltr'
}

interface LanguageState {
  language: Language
  set: (language: Language) => void
}

export const useLanguage = create<LanguageState>((set) => ({
  language: 'en',

  set: (language) => {
    apply(language)
    localStorage.setItem(STORAGE_KEY, language)
    set({ language })

    // A full reload rather than a re-render. Half the tree reads its strings at module scope or
    // holds them in state from a previous render, so patching in place leaves a page that has
    // turned right-to-left with English words still in it — which is worse than either language.
    window.location.reload()
  },
}))

/** Applied before first paint in main.tsx, so the page never flashes the wrong direction. */
export function initLanguage(): void {
  const language = initialLanguage()
  apply(language)
  useLanguage.setState({ language })
}

/**
 * Translates one string, filling in any placeholders.
 *
 * Deliberately a plain function reading the store rather than a hook, so it can be called from
 * anywhere — a column definition, a lookup table, an error handler — not only from inside a
 * component. The full reload on a language change is what makes that safe.
 *
 * A missing translation returns the English, which is the whole point of keying on it.
 */
export function t(text: string, values?: Record<string, string | number>): string {
  const language = useLanguage.getState().language
  const translated = dictionaries[language][text] ?? text

  if (!values) return translated

  return Object.entries(values).reduce(
    (result, [name, value]) => result.replaceAll(`{${name}}`, String(value)),
    translated,
  )
}

/**
 * Translates a message that came back from the server.
 *
 * The API sends its refusals as English sentences — "You are not allowed to give refunds." — and
 * because a key here is the English string, those go through the very same dictionary with no
 * error-code scheme and no second list to keep in step. Anything not translated falls through as
 * English, which is what a cashier would have seen anyway.
 */
export const tServer = t

/**
 * Marks a string for translation without translating it yet.
 *
 * Some strings are written where <code>t()</code> cannot run: the navigation list is a module-level
 * array, evaluated when the module is imported, which is before the language has been read from
 * storage. Translating there would freeze whatever the default happened to be.
 *
 * So the string is marked here and translated at render — <code>t(item.label)</code>. This returns
 * it unchanged; its only job is to be visible to the extractor that checks every string on screen
 * has an Arabic entry, which cannot see a variable.
 */
export const msg = (text: string): string => text
